const AUDIO_SETTINGS_KEY = 'build-destroy-audio-settings'
const DEFAULT_SETTINGS = {
    muted: false,
    musicVolume: 0.42,
    effectsVolume: 0.82
}

const MUSIC_TRACK = 'music_medieval_loop_30s.mp3'

const EFFECT_FILES = {
    slingSnap: [
        'sfx_sling_snap_v1.mp3',
        'sfx_sling_snap_v2.mp3',
        'sfx_sling_snap_v3.mp3',
        'sfx_sling_snap_v4.mp3'
    ],
    blockCollision: [
        'sfx_block_collision_v1.mp3',
        'sfx_block_collision_v2.mp3',
        'sfx_block_collision_v3.mp3',
        'sfx_block_collision_v4.mp3'
    ],
    dartImpact: {
        stone: [
            'sfx_dart_impact_stone_v1.mp3',
            'sfx_dart_impact_stone_v2.mp3',
            'sfx_dart_impact_stone_v3.mp3',
            'sfx_dart_impact_stone_v4.mp3'
        ],
        wood: [
            'sfx_dart_impact_wood_v1.mp3',
            'sfx_dart_impact_wood_v2.mp3',
            'sfx_dart_impact_wood_v3.mp3',
            'sfx_dart_impact_wood_v4.mp3'
        ],
        glass: [
            'sfx_dart_impact_glass_v1.mp3',
            'sfx_dart_impact_glass_v2.mp3',
            'sfx_dart_impact_glass_v3.mp3',
            'sfx_dart_impact_glass_v4.mp3'
        ]
    },
    blockBreak: {
        stone: [
            'sfx_block_break_stone_v1.mp3',
            'sfx_block_break_stone_v2.mp3',
            'sfx_block_break_stone_v3.mp3',
            'sfx_block_break_stone_v4.mp3'
        ],
        wood: [
            'sfx_block_break_wood_v1.mp3',
            'sfx_block_break_wood_v2.mp3',
            'sfx_block_break_wood_v3.mp3',
            'sfx_block_break_wood_v4.mp3'
        ],
        glass: [
            'sfx_block_break_glass_v1.mp3',
            'sfx_block_break_glass_v2.mp3',
            'sfx_block_break_glass_v3.mp3',
            'sfx_block_break_glass_v4.mp3'
        ]
    }
}

const BULLET_AUDIO_PROFILES = {
    basic: { impactMaterial: 'stone', launchRate: 1.08, impactRate: 1.06, volume: 0.92 },
    explosive: { impactMaterial: 'stone', launchRate: 0.82, impactRate: 0.78, volume: 1.14 },
    fire: { impactMaterial: 'wood', launchRate: 1.02, impactRate: 0.96, volume: 1.02 },
    iron: { impactMaterial: 'glass', launchRate: 1.18, impactRate: 1.16, volume: 1.08 }
}

export class GameAudio {
    constructor({ getAssetUrl } = {}) {
        this.getAssetUrl = getAssetUrl || ((fileName) => `/audio/${fileName}`)
        this.settings = readAudioSettings()
        this.music = null
        this.unlocked = false
        this.unlockBound = false
        this.lastPlayedAt = new Map()
        this.nextVariantByGroup = new Map()
        this.effectSources = new Map()
    }

    bindUnlock(targets = []) {
        if (this.unlockBound) {
            return
        }

        this.unlockBound = true
        const unlock = () => {
            void this.resume()
        }
        const options = { passive: true }

        for (const target of [window, document, ...targets]) {
            target?.addEventListener?.('pointerdown', unlock, options)
            target?.addEventListener?.('click', unlock, options)
            target?.addEventListener?.('keydown', unlock, options)
        }

        document.addEventListener('visibilitychange', () => {
            this.applyMusicVolume()
        })
    }

    getState() {
        return {
            muted: this.settings.muted,
            musicVolume: this.settings.musicVolume,
            effectsVolume: this.settings.effectsVolume,
            unlocked: this.unlocked
        }
    }

    async resume() {
        this.unlocked = true
        this.preloadEffects()

        if (this.settings.muted || this.settings.musicVolume <= 0) {
            this.ensureMusic()
            this.applyMusicVolume()
            return
        }

        await this.playMusic()
    }

    toggleMuted() {
        this.updateSettings({ muted: !this.settings.muted })

        if (!this.settings.muted) {
            void this.resume()
        }
    }

    setMusicVolume(value) {
        this.updateSettings({ musicVolume: normalizeVolume(value) })

        if (this.settings.musicVolume > 0 && !this.settings.muted) {
            void this.resume()
        }
    }

    setEffectsVolume(value) {
        this.updateSettings({ effectsVolume: normalizeVolume(value) })
    }

    playSlingSnap(power = 1) {
        this.playEffect('slingSnap', EFFECT_FILES.slingSnap, {
            volumeScale: clamp(power, 0.45, 1.25),
            debounceMs: 55
        })
    }

    playBulletLaunch(type = 'basic', power = 1) {
        const profile = getBulletAudioProfile(type)
        this.playEffect(`slingSnap:${type}`, EFFECT_FILES.slingSnap, {
            volumeScale: clamp(power * profile.volume, 0.45, 1.3),
            playbackRate: profile.launchRate,
            debounceMs: 55
        })
    }

    playBulletImpact(type = 'basic', material = 'stone', intensity = 1) {
        const profile = getBulletAudioProfile(type)
        const audioMaterial = profile.impactMaterial || normalizeMaterial(material)
        this.playEffect(`dartImpact:${type}:${audioMaterial}`, EFFECT_FILES.dartImpact[audioMaterial], {
            volumeScale: clamp(intensity * profile.volume, 0.35, 1.45),
            playbackRate: profile.impactRate,
            debounceMs: 45
        })
    }

    playProjectileImpact(material, intensity = 1) {
        const audioMaterial = normalizeMaterial(material)
        this.playEffect(`dartImpact:${audioMaterial}`, EFFECT_FILES.dartImpact[audioMaterial], {
            volumeScale: clamp(intensity, 0.35, 1.35),
            debounceMs: 45
        })
    }

    playBlockCollision(materialA, materialB, intensity = 1, pairKey = 'block') {
        const volumeScale = clamp(intensity, 0, 1.25)

        if (volumeScale < 0.18) {
            return
        }

        this.playEffect(`blockCollision:${pairKey}`, EFFECT_FILES.blockCollision, {
            volumeScale,
            debounceMs: 90
        })
    }

    playBlockBreak(material, intensity = 1) {
        const audioMaterial = normalizeMaterial(material)
        this.playEffect(`blockBreak:${audioMaterial}`, EFFECT_FILES.blockBreak[audioMaterial], {
            volumeScale: clamp(intensity, 0.55, 1.45),
            debounceMs: 35
        })
    }

    updateSettings(nextSettings) {
        this.settings = {
            ...this.settings,
            ...nextSettings
        }
        writeAudioSettings(this.settings)
        this.applyMusicVolume()
    }

    ensureMusic() {
        if (this.music) {
            return this.music
        }

        const music = new Audio(this.getAssetUrl(MUSIC_TRACK))
        music.loop = true
        music.preload = 'auto'
        music.crossOrigin = 'anonymous'
        music.volume = 0
        this.music = music
        this.applyMusicVolume()
        return music
    }

    async playMusic() {
        const music = this.ensureMusic()
        this.applyMusicVolume()

        if (!music.paused) {
            return
        }

        try {
            await music.play()
        } catch (error) {
            console.warn('Unable to start music', error)
        }
    }

    applyMusicVolume() {
        if (!this.music) {
            return
        }

        const targetVolume = document.hidden || this.settings.muted
            ? 0
            : this.settings.musicVolume
        this.music.volume = clamp(targetVolume, 0, 1)

        if (this.settings.muted || this.settings.musicVolume <= 0) {
            this.music.pause()
        }
    }

    playEffect(groupKey, files, options = {}) {
        if (!this.unlocked || this.settings.muted || this.settings.effectsVolume <= 0) {
            return
        }

        if (!files?.length || !this.canPlay(groupKey, options.debounceMs || 40)) {
            return
        }

        const fileName = this.pickVariant(groupKey, files)
        const source = this.getEffectSource(fileName)
        const player = source.cloneNode(true)
        player.playbackRate = clamp(options.playbackRate || 1, 0.65, 1.35)
        player.volume = clamp(this.settings.effectsVolume * (options.volumeScale || 1), 0, 1)
        player.play().catch((error) => {
            console.warn('Unable to play effect', fileName, error)
        })
    }

    preloadEffects() {
        for (const fileName of getAllEffectFiles()) {
            this.getEffectSource(fileName)
        }
    }

    getEffectSource(fileName) {
        if (this.effectSources.has(fileName)) {
            return this.effectSources.get(fileName)
        }

        const audio = new Audio(this.getAssetUrl(fileName))
        audio.preload = 'auto'
        audio.crossOrigin = 'anonymous'
        this.effectSources.set(fileName, audio)
        return audio
    }

    pickVariant(groupKey, files) {
        const nextIndex = this.nextVariantByGroup.get(groupKey) || 0
        this.nextVariantByGroup.set(groupKey, (nextIndex + 1) % files.length)
        return files[nextIndex % files.length]
    }

    canPlay(key, debounceMs) {
        const now = performance.now()
        const last = this.lastPlayedAt.get(key) || 0

        if (now - last < debounceMs) {
            return false
        }

        this.lastPlayedAt.set(key, now)
        return true
    }
}

function getAllEffectFiles() {
    return [
        ...EFFECT_FILES.slingSnap,
        ...EFFECT_FILES.blockCollision,
        ...Object.values(EFFECT_FILES.dartImpact).flat(),
        ...Object.values(EFFECT_FILES.blockBreak).flat()
    ]
}

function normalizeMaterial(material) {
    if (material === 'glass' || material === 'wood' || material === 'stone') {
        return material
    }

    return material === 'flag' ? 'wood' : 'stone'
}

function getBulletAudioProfile(type) {
    return BULLET_AUDIO_PROFILES[type] || BULLET_AUDIO_PROFILES.basic
}

function readAudioSettings() {
    try {
        const saved = JSON.parse(localStorage.getItem(AUDIO_SETTINGS_KEY) || '{}')

        return {
            muted: Boolean(saved.muted),
            musicVolume: normalizeVolume(saved.musicVolume ?? DEFAULT_SETTINGS.musicVolume),
            effectsVolume: normalizeVolume(saved.effectsVolume ?? DEFAULT_SETTINGS.effectsVolume)
        }
    } catch {
        return { ...DEFAULT_SETTINGS }
    }
}

function writeAudioSettings(settings) {
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify({
        muted: Boolean(settings.muted),
        musicVolume: normalizeVolume(settings.musicVolume),
        effectsVolume: normalizeVolume(settings.effectsVolume)
    }))
}

function normalizeVolume(value) {
    const number = Number(value)

    if (!Number.isFinite(number)) {
        return 0
    }

    return clamp(number, 0, 1)
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}
